import { LazyQueryExecFunction, useLazyQuery, OperationVariables } from '@apollo/client';
import { TArticleCardProps } from 'src/components/ArticleCard/ArticleCard';
import { getArticlesCardData } from 'src/components/ArticleCard/services/getArticlesCardData';
import { useUserTerritoryPermissions } from '../../Territory/services/useUserTerritoryPermissions';
import { TERRITORY_ARTICLES_PAGE_SIZE } from '../constants';
import { getSortedArticlesQuery } from '../../Article/queries/getSortedArticlesQuery';
import { ArticleType } from '../../Article/types';
import { getSortedArticlesForUnauthenticatedUser } from '../../Article/queries/getSortedArticlesForUnauthenticatedUser';

type TUseArticlesProps = {
  territoryId: string;
  userId?: string;
  initialPageSize?: number;
};

type TUseArticlesReturnValue = {
  articles: TArticleCardProps[] | undefined;
  articlesCount: number;
  initialArticlesCount: number;
  hasMoreArticlesToLoad: boolean;
  isLoading: boolean;
  isCalled: boolean;
  onLoadMore: () => void;
  getSortedTerritoryArticles: LazyQueryExecFunction<any, OperationVariables>;
};

export const useTerritoryArticleList = ({
  territoryId,
  userId,
  initialPageSize = TERRITORY_ARTICLES_PAGE_SIZE,
}: TUseArticlesProps): TUseArticlesReturnValue => {
  const { isTerritoryMember } = useUserTerritoryPermissions({ territoryId });

  const query = userId ? getSortedArticlesQuery : getSortedArticlesForUnauthenticatedUser;

  const [getSortedTerritoryArticles, { loading: isLoading, fetchMore, data, called: isCalled }] = useLazyQuery(query, {
    variables: {
      ...(userId && { userId }),
      skip: 0,
      take: initialPageSize,
      where: { type: { equals: ArticleType.Article }, territory: { id: { equals: territoryId } } },
      infiniteScroll: true,
    },
  });

  const { articles = [], articlesCount = 0 } = data?.getSortedArticles || {};
  const mappedArticles = getArticlesCardData(articles, isTerritoryMember);

  const hasMoreArticlesToLoad = articles.length !== articlesCount;

  const fetchMoreArticles = async () => {
    if (!hasMoreArticlesToLoad || isLoading || !articles.length) return;

    await fetchMore({
      variables: {
        skip: articles.length,
      },
    });
  };

  return {
    articles: mappedArticles,
    onLoadMore: fetchMoreArticles,
    hasMoreArticlesToLoad,
    articlesCount,
    initialArticlesCount: initialPageSize,
    isLoading,
    isCalled,
    getSortedTerritoryArticles,
  };
};
